import { ClientData } from "../index";

export interface QuizQuestion {
    id: string;
    question: string;
    options: string[];
    correctAnswer: string;
    type: "demographic" | "metric" | "lifestyle" | "trivia";
}

const getRandomElement = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
const shuffleArray = <T>(arr: T[]): T[] => arr.sort(() => Math.random() - 0.5);

/**
 * Generates a quiz with 5 random questions based on the provided clients.
 */
export const generateQuiz = (clients: ClientData[]): QuizQuestion[] => {
    if (!clients || clients.length < 3) {
        // Need at least a few clients to generate meaningful distractors
        return [];
    }

    const questions: QuizQuestion[] = [];
    const questionTypes = [
        generateAgeQuestion,
        generateHeightQuestion,
        generateWeightQuestion,
        generateActivityLevelQuestion,
        generateBirthdayMonthQuestion,
    ];

    // Generate 5 unique questions
    let attempts = 0;
    while (questions.length < 5 && attempts < 20) {
        attempts++;
        // Pick a random question type
        // In a real app we'd ensure no duplicate question types per quiz or client
        const generator = getRandomElement(questionTypes);
        const question = generator(clients);

        // Avoid duplicate questions by ID
        if (question && !questions.some(q => q.id === question.id)) {
            questions.push(question);
        }
    }

    return questions;
};

// --- Question Generators ---

function generateAgeQuestion(clients: ClientData[]): QuizQuestion | null {
    const subject = getRandomElement(clients);
    if (!subject.birthday) return null;

    const age = calculateAge(subject.birthday);
    if (age === null) return null;

    const correctAnswer = age.toString();

    // Distractors: other clients' ages or random close numbers
    const distractors = new Set<string>();
    while (distractors.size < 3) {
        const randomClient = getRandomElement(clients);
        const otherAge = calculateAge(randomClient.birthday);
        if (otherAge !== null && otherAge !== age) {
            distractors.add(otherAge.toString());
        } else {
            // Fallback: generate fake age close to real age
            let fakeAge = age + (Math.random() > 0.5 ? 1 : -1) * Math.floor(Math.random() * 5 + 1);
            if (fakeAge < 18) fakeAge = 18;
            distractors.add(fakeAge.toString());
        }
    }

    const options = shuffleArray([correctAnswer, ...Array.from(distractors)]);

    return {
        id: `age-${subject.clientUserId}-${Date.now()}`,
        question: `How old is ${subject.name}?`,
        options,
        correctAnswer,
        type: "demographic",
    };
}

function generateHeightQuestion(clients: ClientData[]): QuizQuestion | null {
    const subject = getRandomElement(clients);
    if (!subject.height) return null;

    const heightStr = cmToFeetInches(subject.height);
    const correctAnswer = heightStr;

    const distractors = new Set<string>();
    while (distractors.size < 3) {
        const randomClient = getRandomElement(clients);
        if (randomClient.height && randomClient.height !== subject.height) {
            distractors.add(cmToFeetInches(randomClient.height));
        } else {
            // Fallback
            const fakeHeight = subject.height + (Math.random() > 0.5 ? 1 : -1) * Math.floor(Math.random() * 10 + 2);
            distractors.add(cmToFeetInches(fakeHeight));
        }
    }

    const options = shuffleArray([correctAnswer, ...Array.from(distractors)]);

    return {
        id: `height-${subject.clientUserId}-${Date.now()}`,
        question: `What is ${subject.name}'s height?`,
        options,
        correctAnswer,
        type: "metric",
    };
}

function generateWeightQuestion(clients: ClientData[]): QuizQuestion | null {
    const subject = getRandomElement(clients);
    if (!subject.weight) return null;

    const weightStr = `${Math.round(subject.weight * 2.20462)} lbs`; // Convert kg to lbs
    const correctAnswer = weightStr;

    const distractors = new Set<string>();
    while (distractors.size < 3) {
        const randomClient = getRandomElement(clients);
        if (randomClient.weight && randomClient.weight !== subject.weight) {
            distractors.add(`${Math.round(randomClient.weight * 2.20462)} lbs`);
        } else {
            // Fallback
            const fakeWeight = subject.weight + (Math.random() > 0.5 ? 1 : -1) * Math.floor(Math.random() * 5 + 2);
            distractors.add(`${Math.round(fakeWeight * 2.20462)} lbs`);
        }
    }

    const options = shuffleArray([correctAnswer, ...Array.from(distractors)]);

    return {
        id: `weight-${subject.clientUserId}-${Date.now()}`,
        question: `How much does ${subject.name} weigh?`,
        options,
        correctAnswer,
        type: "metric",
    };
}

function generateActivityLevelQuestion(clients: ClientData[]): QuizQuestion | null {
    const subject = getRandomElement(clients);
    const levels = ["sedentary", "lightly active", "moderately active", "very active", "extra active"];

    if (!subject.activityLevel) return null;

    const correctAnswer = subject.activityLevel;
    const distractors = levels.filter(l => l !== correctAnswer).sort(() => 0.5 - Math.random()).slice(0, 3);

    const options = shuffleArray([correctAnswer, ...distractors]);

    return {
        id: `activity-${subject.clientUserId}-${Date.now()}`,
        question: `What is ${subject.name}'s activity level?`,
        options,
        correctAnswer,
        type: "lifestyle",
    };
}

function generateBirthdayMonthQuestion(clients: ClientData[]): QuizQuestion | null {
    const subject = getRandomElement(clients);
    if (!subject.birthday) return null;

    const date = new Date(subject.birthday);
    const month = date.toLocaleString('default', { month: 'long' });
    const correctAnswer = month;

    const allMonths = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const distractors = allMonths.filter(m => m !== correctAnswer).sort(() => 0.5 - Math.random()).slice(0, 3);

    const options = shuffleArray([correctAnswer, ...distractors]);

    return {
        id: `bday-${subject.clientUserId}-${Date.now()}`,
        question: `In which month is ${subject.name}'s birthday?`,
        options,
        correctAnswer,
        type: "demographic",
    };
}

// --- Helpers ---

function calculateAge(birthday: Date | string | null | undefined): number | null {
    if (!birthday) return null;
    const dob = new Date(birthday);
    if (Number.isNaN(dob.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
        age--;
    }
    return age;
}

function cmToFeetInches(cm?: number | null): string {
    if (!cm || Number.isNaN(cm)) return "—";
    const totalInches = Math.round(cm / 2.54);
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;
    return `${feet}' ${inches}"`;
}
